import { useCallback, useEffect, useState } from "react";
import { getMyEmailMarketingPermissionsAPI } from "../../auth/apiEmailMarketing";
import { isAuthenticated } from "../../auth";

// One shared, cached reader of GET /email-marketing/permissions/me for every
// Email Marketing surface (account menu, My Business nav, User Settings card,
// route guards, Dashboard, Editor, template library). The server resolves
// every flag from the caller's effective active role + the Family-managed
// settings (global access is already the parent of each template flag), so
// nothing here re-derives a permission from a role name -- this is UI only;
// the backend re-checks every request.
//
// Restricted actions are never shown while loading: until the first
// response arrives every flag reads false.

export const EMPTY_EMAIL_MARKETING_ACCESS = Object.freeze({
  canAccessEmailMarketing: false,
  canCreateCampaigns: false,
  canManageMyTemplates: false,
  canSaveAsTemplate: false,
  canAccessElakayTemplates: false,
  canManageGlobalTemplates: false,
  canCreateGlobalTemplates: false,
  canAccessTemplateDesigner: false,
});

const FRESH_MS = 15000;
const FOCUS_REFRESH_MS = 5000;

let cache = null; // { token, at, data }
let inflight = null; // { token, promise }
const listeners = new Set();

const toData = (res) => ({
  permissions: res?.permissions || {},
  templatePermissions: res?.templatePermissions || {},
  access: { ...EMPTY_EMAIL_MARKETING_ACCESS, ...(res?.emailMarketingPermissions || {}) },
});

const DENIED = Object.freeze(toData(null));

const publish = (entry) => listeners.forEach((listener) => listener(entry));

const load = (token) => {
  if (inflight && inflight.token === token) return inflight.promise;
  const promise = Promise.resolve()
    .then(() => getMyEmailMarketingPermissionsAPI(token))
    .then((res) => {
      cache = { token, at: Date.now(), data: toData(res), error: null };
      return cache;
    })
    .catch((error) => {
      // Fail closed, but keep the error so a guard can show "unable to
      // verify" instead of treating a network blip as an explicit denial.
      cache = { token, at: Date.now(), data: DENIED, error };
      return cache;
    })
    .finally(() => { if (inflight?.promise === promise) inflight = null; });
  inflight = { token, promise };
  promise.then(publish);
  return promise;
};

/** Forces every mounted consumer to re-read (e.g. after Family saves template permissions). */
export const refreshEmailMarketingPermissions = () => {
  const token = isAuthenticated()?.token;
  cache = null;
  return token ? load(token) : Promise.resolve(null);
};

/** Test helper: drops the module-level cache between test cases. */
export const resetEmailMarketingPermissionsCache = () => {
  cache = null;
  inflight = null;
};

/**
 * @param {{ enabled?: boolean }} [options] - pass enabled:false to skip the
 *   request entirely (e.g. a role that can never reach Email Marketing).
 * @returns {{ loading: boolean, error: Error|null, permissions: object, templatePermissions: object, access: object, refresh: Function }}
 */
const useEmailMarketingPermissions = ({ enabled = true } = {}) => {
  const token = isAuthenticated()?.token || "";
  const active = Boolean(enabled && token);
  const cached = active && cache?.token === token ? cache : null;
  const [entry, setEntry] = useState(cached);

  useEffect(() => {
    if (!active) return undefined;
    const listener = (next) => { if (next?.token === token) setEntry(next); };
    listeners.add(listener);
    if (cache?.token === token) setEntry(cache);
    if (!cache || cache.token !== token || Date.now() - cache.at > FRESH_MS) load(token);
    const onFocus = () => {
      if (!cache || Date.now() - cache.at > FOCUS_REFRESH_MS) load(token);
    };
    window.addEventListener("focus", onFocus);
    return () => {
      listeners.delete(listener);
      window.removeEventListener("focus", onFocus);
    };
  }, [active, token]);

  const refresh = useCallback(() => {
    if (!active) return Promise.resolve(null);
    cache = null;
    return load(token);
  }, [active, token]);

  if (!active) return { loading: false, error: null, ...DENIED, refresh };
  const current = entry && entry.token === token ? entry : null;
  return {
    loading: !current,
    error: current?.error || null,
    ...(current?.data || DENIED),
    refresh,
  };
};

export default useEmailMarketingPermissions;
